import { createEffect, location, onCleanup, onMount, reactive } from 'cdui-js';
import { replaceTemplate } from '@china-difi/lib/template';
import { composeStockTransferSettlementSignature, submitStockTransferInApply } from '../../../api/stockTransferIn';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import i18n from '../../../i18n';
import { blockBack } from '../../../lib/browser';
import { isH5User } from '../../../stores/user';
import { fillSignatureBoard, paintSignatureHint } from '../account/paintSignatureHint';
import { showToast, showToastError } from '../components/Toast';
import { StockTransferNav } from './Nav';
import {
  dataURLtoFile,
  finalSignatureBase64,
  getSignatureFallbackSize,
  SIGNATURE_EDGE_INSET,
  SIGNATURE_MOBILE_HEADER,
} from './signatureCanvas';
import {
  clearStockTransferSession,
  getStockTransferApplyId,
  getStockTransferDraft,
  readMarketQuery,
  setStockTransferSubmittedSnapshot,
  syncStockTransferAppShell,
  withStockTransferQuery,
} from './shared';
import './page.css';

export const StockTransferInSignature = () => {
  const state = reactive({
    loading: false,
  });
  const layout = reactive(getSignatureFallbackSize());

  let canvasRef: HTMLCanvasElement | null = null;
  let rootRef: HTMLDivElement | null = null;
  let isDrawing = false;
  let ctx: CanvasRenderingContext2D | null = null;
  let lastX = 0;
  let lastY = 0;
  let hasStroke = false;

  const applyId = () => getStockTransferApplyId();

  const paintEmptyBoard = (cssWidth: number, cssHeight: number) => {
    if (!ctx) return;
    fillSignatureBoard(ctx, cssWidth, cssHeight);
    paintSignatureHint(ctx, cssWidth, cssHeight, i18n.OpenProgress.Transfer.Signature.SignHintPrefix);
  };

  const syncSignatureLayout = () => {
    const el = rootRef;
    const shell = document.getElementById('h5-shell');
    const width = el?.clientWidth || shell?.clientWidth || window.innerWidth;
    let height: number;
    if (el && shell) {
      height = Math.max(0, Math.round(shell.getBoundingClientRect().bottom - el.getBoundingClientRect().top));
    } else if (shell?.classList.contains('h5-shell--pc')) {
      height = Math.max(0, shell.clientHeight - SIGNATURE_MOBILE_HEADER);
    } else {
      height = Math.max(0, window.innerHeight - SIGNATURE_MOBILE_HEADER);
    }
    if (width > 0) layout.width = width;
    if (height > 0) layout.height = height;
  };

  const generateSignature = (): string => {
    const canvas = canvasRef;
    if (!canvas || !hasStroke) return '';
    return finalSignatureBase64(canvas);
  };

  const handleSubmit = async () => {
    if (state.loading) return;
    const id = applyId();
    if (!id) {
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }
    isDrawing = false;
    state.loading = true;
    try {
      const base64 = generateSignature();
      if (!base64) {
        showToast(i18n.OpenProgress.Transfer.Signature.ToastCompleteFirst);
        return;
      }
      const signFile = dataURLtoFile(base64, 'sign.png');
      await composeStockTransferSettlementSignature(id, signFile);

      const draft = getStockTransferDraft();
      const market = readMarketQuery();
      const selectedStocks = (draft?.stocks || []).filter((item) => item.selected);
      if (!draft || !selectedStocks.length) {
        showToast(i18n.StockTransferIn.LoadFail);
        return;
      }

      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试
      }
      await submitStockTransferInApply({ id });

      setStockTransferSubmittedSnapshot({
        applyId: id,
        submittedAt: Date.now(),
        market,
        draft: { ...draft, applyId: id },
        selectedStocks,
        status: 'SUBMITTED',
      });
      clearStockTransferSession();
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/submitted', { market }), [0, 0]);
    } catch (e) {
      showToastError(e);
    } finally {
      state.loading = false;
    }
  };

  createEffect(() => {
    const canvas = canvasRef;
    if (!canvas) return;
    void layout.width;
    void layout.height;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    hasStroke = false;
    paintEmptyBoard(rect.width, rect.height);
  });

  onMount(() => {
    syncStockTransferAppShell();
    const id = applyId();
    if (!id) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in'), [0, 0]);
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      bodyTouchAction: body.style.touchAction,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    syncSignatureLayout();
    const shell = document.getElementById('h5-shell');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncSignatureLayout()) : null;
    if (ro) {
      if (rootRef) ro.observe(rootRef);
      if (shell) ro.observe(shell);
    }
    window.addEventListener('resize', syncSignatureLayout);
    const unlockBack = isH5User() ? blockBack() : undefined;

    onCleanup(() => {
      ro?.disconnect();
      window.removeEventListener('resize', syncSignatureLayout);
      unlockBack?.();
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      body.style.touchAction = prev.bodyTouchAction;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    });
  });

  const startDraw = (e: MouseEvent | TouchEvent) => {
    if (state.loading || !canvasRef) return;
    if (e.cancelable) e.preventDefault();
    isDrawing = true;
    const { clientX, clientY } = 'touches' in e ? e.touches[0] : e;
    const rect = canvasRef.getBoundingClientRect();
    lastX = clientX - rect.left;
    lastY = clientY - rect.top;
    if (!hasStroke && ctx) {
      fillSignatureBoard(ctx, rect.width, rect.height);
      hasStroke = true;
    }
  };

  const drawing = (e: MouseEvent | TouchEvent) => {
    if (e.cancelable) e.preventDefault();
    if (state.loading || !isDrawing || !ctx || !canvasRef) return;
    const { clientX, clientY } = 'touches' in e ? e.touches[0] : e;
    const rect = canvasRef.getBoundingClientRect();
    const currentX = clientX - rect.left;
    const currentY = clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();
    lastX = currentX;
    lastY = currentY;
  };

  const endDraw = () => {
    isDrawing = false;
  };

  const clearCanvas = () => {
    if (state.loading || !canvasRef || !ctx) return;
    const rect = canvasRef.getBoundingClientRect();
    hasStroke = false;
    paintEmptyBoard(rect.width, rect.height);
  };

  const goBack = () => {
    const market = readMarketQuery();
    location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/confirm', { market }), [0, 0]);
  };

  return (
    <div class="stock-transfer-in-h5 sti-signature-page">
      <StockTransferNav title={i18n.StockTransferIn.SignatureTitle} onBack={goBack} />
      <div
        class="sti-signature-shell"
        ref={(el) => {
          rootRef = el;
        }}
        style={{
          position: 'relative',
          width: '100%',
          height: `${layout.height}px`,
          overflow: 'hidden',
          'touch-action': 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: `${layout.height}px`,
            height: `${Math.max(0, layout.width - SIGNATURE_EDGE_INSET * 2)}px`,
            overflow: 'hidden',
            transform: 'translate(-50%, -50%) rotate(90deg)',
          }}
        >
          <div class="flex flex-column height-100">
            <div class="flex justify-center align-center gap-12px" style={{ height: '40px' }}>
              {replaceTemplate(i18n.StockTransferIn.SignatureConsent, {
                company: i18n.OpenProgress.Common.DefaultCompanyName,
              })}
              <button
                type="button"
                class="default-btn padding-x"
                style={{ height: '32px' }}
                onclick={clearCanvas}
                disabled={state.loading}
              >
                {i18n.OpenProgress.Common.Rewrite}
              </button>
            </div>
            <div style={{ flex: 1 }} />
            <div class="flex justify-center">
              <div
                class="flex padding-x-L align-center"
                style={{
                  gap: '10px',
                  height: '60px',
                  width: `${Math.max(0, layout.width - SIGNATURE_EDGE_INSET * 2)}px`,
                }}
              >
                <button
                  type="button"
                  class="default-btn btn-rounded"
                  style={{ height: '48px', flex: 1 }}
                  onclick={goBack}
                  disabled={state.loading}
                >
                  {i18n.OpenProgress.Common.Prev}
                </button>
                <button
                  type="button"
                  class="primary-btn btn-rounded"
                  style={{ height: '48px', flex: 2 }}
                  onclick={handleSubmit}
                  disabled={state.loading}
                >
                  {state.loading ? i18n.StockTransferIn.Submitting : i18n.StockTransferIn.Submit}
                </button>
              </div>
            </div>
          </div>
        </div>
        <canvas
          ref={(el) => {
            canvasRef = el;
          }}
          class="border"
          style={{
            position: 'absolute',
            right: `${40 + SIGNATURE_EDGE_INSET}px`,
            top: 0,
            width: `calc(100% - 70px - 40px - ${SIGNATURE_EDGE_INSET * 2}px)`,
            height: `${layout.height}px`,
            'touch-action': 'none',
            'pointer-events': state.loading ? 'none' : 'auto',
          }}
          onmousedown={startDraw}
          onmousemove={drawing}
          onmouseup={endDraw}
          onmouseleave={endDraw}
          ontouchstart={startDraw}
          ontouchmove={drawing}
          ontouchend={endDraw}
        />
      </div>
    </div>
  );
};
